/**
 * Phase of the assistant message streaming lifecycle.
 * - thinking: backend is processing (status/progress events arriving)
 * - answering: first real content chunk arrived, accumulating text
 * - done: stream finished normally
 * - error: stream ended with an error
 */
export type AssistantPhase = 'thinking' | 'answering' | 'done' | 'error';

export interface ConversationImageAttachment {
  id: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  preview_url: string;
  file?: File;
}

export interface ConversationImageReference {
  attachment_id: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  width?: number;
  height?: number;
}

export interface ImageAnalysisStatus {
  status?: string;
  notice?: string;
  total_images?: number;
  processed_images?: number;
  skipped_by_size?: number;
  model?: string;
  fallback_reason?: string;
  error_type?: string;
  vision_context_chars?: number;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'status';
  content: string;
  timestamp: Date;
  isStatus?: boolean;
  useCanvas?: boolean;
  analysisArtifacts?: AnalysisArtifacts | null;
  currentStatus?: string;
  isStreaming?: boolean;
  streamUpdatedAt?: number;
  /** True when the backend signalled artifacts are still being generated */
  artifactsGenerating?: boolean;
  /** Granular phase of the streaming lifecycle */
  assistantPhase?: AssistantPhase;
  /** User uploaded images associated with this message */
  attachments?: ConversationImageAttachment[];
  /** Backend image analysis metadata for visual/fallback feedback */
  imageAnalysis?: ImageAnalysisStatus | null;
}

export interface AnalysisArtifacts {
  xlsx_base64?: string;
  csv_base64?: string;
  pdf_base64?: string;
  docx_base64?: string;
  pptx_base64?: string;
  json_base64?: string;
  xlsx_artifact_id?: string;
  csv_artifact_id?: string;
  pdf_artifact_id?: string;
  docx_artifact_id?: string;
  pptx_artifact_id?: string;
  json_artifact_id?: string;
  xlsx_download_url?: string;
  csv_download_url?: string;
  pdf_download_url?: string;
  docx_download_url?: string;
  pptx_download_url?: string;
  json_download_url?: string;
  xlsx_file_name?: string;
  csv_file_name?: string;
  pdf_file_name?: string;
  docx_file_name?: string;
  pptx_file_name?: string;
  json_file_name?: string;
  xlsx_mime_type?: string;
  csv_mime_type?: string;
  pdf_mime_type?: string;
  docx_mime_type?: string;
  pptx_mime_type?: string;
  json_mime_type?: string;
  xlsx_is_complete?: boolean;
  csv_is_complete?: boolean;
  pdf_is_complete?: boolean;
  docx_is_complete?: boolean;
  pptx_is_complete?: boolean;
  json_is_complete?: boolean;
  is_complete?: boolean;
  truncated?: boolean;
  total_rows?: number;
  preview_rows?: number;
  artifact_total_rows?: number;
}

export type SearchContextType = 'global' | 'specific' | 'web' | 'connectors';
export const DEFAULT_SEARCH_CONTEXT_TYPE: SearchContextType = 'web';

/** Contexto para PUT /v1/execute-ai/canvas-session-state (última mensagem canvas). */
export type CanvasSessionPersistContext = {
  crewId: number;
  sessionId: string;
};

export interface ConversationTab {
  id: string;
  name: string;
  backendConversationId?: number | null;
  selectedCrewId: number | null;
  selectedLlmModel: string | null;
  draftMessage: string;
  useCanvas: boolean;
  messages: Message[];
  selectedKnowledgeSources: import('./entities').KnowledgeSource[];
  selectedConnectorSources?: import('./entities').ConnectorSource[];
  searchContextType: SearchContextType;
  hasExplicitSearchContextSelection?: boolean;
  useLiveData: boolean;
  sessionId: string | null;
  // True when the user just created the tab on the client side; the
  // sessionId points to a backend session that does not exist yet, so we
  // skip the history hydration GET to avoid a 404 polling loop.
  createdLocally?: boolean;
  ragConfig: RagConfig;
}

export interface RagConfig {
  rag_top_k: number;
  rag_similarity_threshold: number;
  rag_tags: string[];
  rag_max_context_length: number;
}

export interface ConversationState {
  conversationTabs: ConversationTab[];
  activeTabId: string | null;
  isInitialized: boolean;
}

export interface MessageInputProps {
  message: string;
  setMessage: (message: string) => void;
  onSendMessage: (attachments?: ConversationImageAttachment[]) => void;
  onStopMessage?: () => void;
  isStreaming: boolean;
  disabled?: boolean;
  useCanvas?: boolean;
  onUseCanvasChange?: (useCanvas: boolean) => void;
  searchContextType?: SearchContextType;
  onSearchContextTypeChange?: (value: SearchContextType) => void;
  useLiveData?: boolean;
  onUseLiveDataChange?: (value: boolean) => void;
  selectedKnowledgeSources?: import('./entities').KnowledgeSource[];
  selectedConnectorSources?: import('./entities').ConnectorSource[];
  onOpenKnowledgeBaseModal?: () => void;
  selectedLlmModel?: string | null;
  llmModels?: import('./entities').LlmCatalogModel[];
  llmDefaultModel?: string | null;
  llmLoading?: boolean;
  llmLoadError?: boolean;
  onSelectedLlmModelChange?: (value: string | null) => void;
  textareaHeightPx?: number;
}

export interface MessageBubbleProps {
  message: Message;
  formatTime: (date: Date | string) => string;
}

export interface ConversationHeaderProps {
  selectedCrewId: number | null;
  crewName?: string;
}

export interface ConversationSidebarProps {
  selectedKnowledgeSources: import('./entities').KnowledgeSource[];
  onRemoveKnowledgeSource: (sourceId: number) => void;
  onOpenKnowledgeModal: () => void;
}
